import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  getRedirectRoute,
  getStepConfig,
  getNextStep,
  getPreviousStep,
  isOnboardingComplete,
  OnboardingStep,
  ONBOARDING_STEPS,
} from "@/utils/onboardingConfig";
import { getPrimaryRole } from "@/utils/roleUtils";
import { isAddAccountMode } from "@/utils/auth";

interface OnboardingGuardProps {
  children: ReactNode;
}

const allOnboardingRoutes = Object.values(ONBOARDING_STEPS)
  .filter((config) => config.step !== "complete") // Exclude "complete" which points to /home
  .map((config) => config.route);

export const OnboardingGuard = ({ children }: OnboardingGuardProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const currentRoute = location.pathname;

  // Show loading state while auth is loading
  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="h-20 w-20 animate-spin rounded-full border-8 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  // Signed out: to /login, carrying the page they asked for (as
  // ProtectedRoute does) so they come back to it after signing in. /signup
  // stays open to signed-out visitors — that is where accounts start.
  if (!user && currentRoute !== "/signup") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Get user's onboarding step and role
  const userStep = (user?.onboardingStep as OnboardingStep) || null;
  const userRole = user ? getPrimaryRole(user).toLowerCase() : undefined;

  // DISABLED (multi-account): always false now — isAddAccountMode() is a stub.
  // It let an add-account tab reach onboarding routes while the signed-in user
  // had already completed onboarding.
  const addAccountMode = isAddAccountMode();

  // Check what type of route this is
  const isOnboardingRoute = isRouteInOnboardingFlow(currentRoute);
  // Not a bare === "complete" check: a step that this role can never finish
  // (retired in v3, or belonging to the other role) must not gate the app.
  const isUserComplete = isOnboardingComplete(userStep, userRole);

  // CASE 1: User has completed onboarding (step === "complete")
  if (isUserComplete) {
    // In add-account mode, allow onboarding routes through so the new
    // account can be signed up even though the current user is complete.
    if (isOnboardingRoute && !addAccountMode) {
      return <Navigate to="/home" replace />;
    }
    // Allow access to all other routes (dashboard, profile, etc.)
    return <>{children}</>;
  }

  // CASE 2: User has NOT completed onboarding
  // They can only access their current onboarding step

  // Get the route user should be on based on their step
  const allowedRoute = getRedirectRoute(userStep, userRole);

  // If user is on their allowed route, let them through
  if (currentRoute === allowedRoute) {
    return <>{children}</>;
  }

  // Allow /signup for users who haven't started (no step yet)
  if (currentRoute === "/signup" && !userStep) {
    return <>{children}</>;
  }

  // Allow access to the next step's route — handles the transition window where
  // the backend step update may lag behind the in-app navigation (e.g. step 3 → step 4)
  const nextStepKey = getNextStep(userStep, userRole);
  const nextStepRoute = nextStepKey ? getStepConfig(nextStepKey)?.route : null;
  if (nextStepRoute && currentRoute === nextStepRoute) {
    return <>{children}</>;
  }

  // Allow access to the previous step's route — enables Back button navigation
  const prevStepKey = getPreviousStep(userStep);
  const prevStepRoute = prevStepKey ? getStepConfig(prevStepKey)?.route : null;
  if (prevStepRoute && currentRoute === prevStepRoute) {
    return <>{children}</>;
  }

  // User is trying to access a route they shouldn't - redirect to their step
  return <Navigate to={allowedRoute} replace />;
};

/**
 * Check if a route is part of the onboarding flow
 * Includes: /signup, /signup/2, /signup/3, /signup/4, /onboarding/goal-setting, /onboarding/advisor-matching
 */
function isRouteInOnboardingFlow(route: string): boolean {
  return allOnboardingRoutes.includes(route);
}
